import axios from 'axios'
import { toast } from 'react-toastify'
import { logoutHandler } from 'src/consumer/services/loginLogoutHandlers'
import { isClientLogoutInProgress } from 'src/shared/auth/logoutSignals'
import { getLoginPath } from 'src/services/APIs/Login/Login'

// Flag to prevent multiple simultaneous logout attempts
let isLoggingOut = false
let logoutTimer = null

// Set true while a chamber-screen / kiosk layout is mounted. Such devices own their re-login UX
// (a prefilled, non-navigating dialog that preserves the device config so they can re-register), so
// a 401 there must NOT run the usual logout+redirect / global modal.
let kioskScreenActive = false
export function setKioskScreenActive(active) {
  kioskScreenActive = Boolean(active)
}

// Global event name to trigger login modal (for consumer portal)
const LOGIN_MODAL_EVENT = 'show-login-modal-for-401'
const SUPPORT_TWO_FACTOR_EVENT = 'show-support-twofactor-prompt'
// Event the chamber-screen layouts listen for to open their own re-login dialog on a 401.
const KIOSK_RELOGIN_EVENT = 'kiosk-relogin-needed'
export function getKioskReloginEventName() {
  return KIOSK_RELOGIN_EVENT
}

/**
 * Determines if current portal should use modal or redirect
 * Consumer portal uses modal, other portals use redirect
 */
function shouldUseModal() {
  const pathname = window.location.pathname
  
  // Consumer portal uses modal
  if (
    pathname === '/' || 
    pathname.startsWith('/course') ||
    pathname.startsWith('/technique') ||
    pathname.startsWith('/article') ||
    pathname.startsWith('/assessment') ||
    pathname.startsWith('/timer') ||
    pathname.startsWith('/journal') ||
    pathname.startsWith('/onboarding') ||
    pathname.startsWith('/booking') ||
    pathname.startsWith('/gift-card') ||
    pathname.startsWith('/account') ||
    (!pathname.startsWith('/workplace') &&
     !pathname.startsWith('/support-home') &&
     !pathname.startsWith('/clinic-home') &&
     !pathname.startsWith('/mentor-home') &&
     !pathname.startsWith('/ca-home') &&
     !pathname.startsWith('/kiosk'))
  ) {
    return true
  }
  
  // Business, Clinic, Mentor, Support portals use redirect
  return false
}

/**
 * Determines the appropriate login route based on current pathname
 * @returns {string} Login route path
 */
function getLoginRoute() {
  const pathname = window.location.pathname
  
  // Business portal
  if (pathname.startsWith('/workplace') || pathname.startsWith('/dashboard')) {
    return '/workplace/login'
  }
  
  // Support portal
  if (pathname.startsWith('/support-home')) {
    return '/support-home/login'
  }
  
  // Clinic portal
  if (pathname.startsWith('/clinic-home')) {
    return '/clinic-home/login'
  }
  
  // Mentor portal
  if (pathname.startsWith('/mentor-home')) {
    return '/mentor-home/login'
  }
  
  // Clinic Assistant or Kiosk
  if (pathname.startsWith('/ca-home') || pathname.startsWith('/kiosk')) {
    return '/aumhum-for-clinics'
  }
  
  // Default to consumer login
  return '/login'
}

/**
 * Validates redirect URL to prevent infinite loops and security issues
 * @param {string} url - URL to validate
 * @returns {boolean} True if URL is safe to use as redirect
 */
function isValidRedirectUrl(url) {
  if (!url || typeof url !== 'string') {
    return false
  }
  
  // Prevent redirect to login pages (infinite loop protection)
  const loginPaths = [
    '/login',
    '/workplace/login',
    '/support-home/login',
    '/clinic-home/login',
    '/mentor-home/login',
    '/aumhum-for-clinics',
  ]
  
  const isLoginPage = loginPaths.some(path => url.startsWith(path))
  if (isLoginPage) {
    return false
  }
  
  // Ensure URL is relative (same origin) - prevent open redirects
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
    return false
  }
  
  // Ensure URL starts with / (relative path)
  if (!url.startsWith('/')) {
    return false
  }
  
  return true
}

/**
 * Gets a safe redirect URL, falling back to home if current URL is invalid
 * @param {string} currentUrl - Current URL path
 * @returns {string} Safe redirect URL
 */
function getSafeRedirectUrl(currentUrl) {
  if (isValidRedirectUrl(currentUrl)) {
    return currentUrl
  }
  
  // Fallback to home page if current URL is not safe
  return '/'
}

/**
 * Handles 401 Unauthorized responses gracefully
 * - Shows user-friendly message
 * - Clears authentication tokens
 * - Shows modal (consumer) or redirects (other portals)
 * - Preserves current URL for post-login redirect
 */
function handle401Unauthorized(error) {
  if (isClientLogoutInProgress()) {
    return Promise.reject(error)
  }

  // Chamber-screen / kiosk: don't logout+redirect or pop the global modal. Just drop the dead token
  // (so the screen's token check surfaces its own dialog) and signal the layout to open its
  // prefilled, non-navigating re-login dialog. The device config (ROLE_SCREEN_CONFIG,
  // AUMHUM_SCREEN_ID, room) is preserved so re-login can re-register the same screen.
  if (kioskScreenActive) {
    localStorage.removeItem('token')
    localStorage.removeItem('accessTokenExpiry')
    localStorage.removeItem('accessTokenExpiryReadable')
    window.dispatchEvent(new CustomEvent(KIOSK_RELOGIN_EVENT))
    return Promise.reject(error)
  }

  // Prevent multiple simultaneous logout attempts
  if (isLoggingOut) {
    return Promise.reject(error)
  }
  
  // Clear any pending logout timer
  if (logoutTimer) {
    clearTimeout(logoutTimer)
    logoutTimer = null
  }
  
  isLoggingOut = true
  
  // Get current URL for redirect after login and validate it
  const currentUrl = window.location.pathname + window.location.search
  const safeRedirectUrl = getSafeRedirectUrl(currentUrl)
  
  // Show user-friendly message
  const errorMessage = error?.response?.data?.message || 
    'Your session has expired. Please log in again to continue.'
  
  toast.error(errorMessage, {
    autoClose: 5000,
    toastId: 'session-expired', // Prevent duplicate toasts
  })
  
  // Clear authentication tokens
  const removeLocalStorageExceptKeys = []
  const removeSessionStorageExceptKeys = []
  
  // Preserve lastChosenFlow for dual-role users if on clinic-assistant/kiosk routes
  if (currentUrl.includes('/ca-home') || currentUrl.includes('/kiosk')) {
    const lastChosenFlow = localStorage.getItem('lastChosenFlow')
    if (lastChosenFlow === 'kiosk' || lastChosenFlow === 'clinic-assistant') {
      removeLocalStorageExceptKeys.push('lastChosenFlow')
    }
  }
  
  logoutHandler(removeLocalStorageExceptKeys, removeSessionStorageExceptKeys)
  
  // Choose between modal or redirect based on portal
  if (shouldUseModal()) {
    // Consumer portal: Dispatch event to show modal
    // The Global401LoginModal component will listen to this event
    window.dispatchEvent(new CustomEvent(LOGIN_MODAL_EVENT, {
      detail: { redirectTo: safeRedirectUrl }
    }))
    
    // Reset flag after a delay to allow modal to open
    setTimeout(() => {
      isLoggingOut = false
    }, 1000)
  } else {
    // Other portals: Redirect to login page
    const loginRoute = getLoginRoute()
    // Only add redirect param if it's valid and not the home page
    const redirectParam = safeRedirectUrl && safeRedirectUrl !== '/' 
      ? encodeURIComponent(safeRedirectUrl)
      : null
    const loginUrl = redirectParam
      ? `${loginRoute}?redirect=${redirectParam}`
      : loginRoute
    
    logoutTimer = setTimeout(() => {
      window.location.href = loginUrl
      setTimeout(() => {
        isLoggingOut = false
      }, 1000)
    }, 1500)
  }
  
  return Promise.reject(error)
}

/**
 * Returns true if the failing request actually carried a bearer token.
 *
 * A 401 on a request that NEVER included an Authorization header is
 * "missing credentials", not "session expired". Public marketing pages
 * occasionally make defensive calls to secure endpoints (for example
 * GET /api/secure/user/profile to pre-fill a form) — firing the full
 * logout + toast + global login-modal flow for an anonymous visitor in
 * that case is both confusing and incorrect.
 *
 * Axios headers can live in a few shapes (object, AxiosHeaders class,
 * or under `common`). We look for any Bearer value across those.
 *
 * Several call sites build the header as `Bearer ${localStorage.getItem('token')}`
 * which yields the literal strings "Bearer null" / "Bearer undefined" when the
 * visitor is anonymous. Treat those as unauthenticated.
 */
function wasRequestAuthenticated(config) {
  if (!config) return true // conservative: can't tell, preserve legacy behavior

  const headers = config.headers || {}
  const candidates = []

  // AxiosHeaders instances expose .get(), plain objects we read directly.
  if (typeof headers.get === 'function') {
    candidates.push(headers.get('Authorization'))
    candidates.push(headers.get('authorization'))
  }
  candidates.push(headers.Authorization)
  candidates.push(headers.authorization)
  if (headers.common) {
    candidates.push(headers.common.Authorization)
    candidates.push(headers.common.authorization)
  }

  return candidates.some((v) => {
    if (typeof v !== 'string') return false
    const trimmed = v.trim()
    if (!trimmed.toLowerCase().startsWith('bearer ')) return false
    const tokenPart = trimmed.slice(7).trim()
    if (!tokenPart) return false
    const lowered = tokenPart.toLowerCase()
    if (lowered === 'null' || lowered === 'undefined') return false
    return true
  })
}

function handleSupportTwoFactorRequired(error) {
  window.dispatchEvent(
    new CustomEvent(SUPPORT_TWO_FACTOR_EVENT, {
      detail: {
        requestUrl: error?.config?.url || '',
      },
    }),
  )
  return Promise.reject(error)
}

/**
 * Setup axios response interceptor to handle 401 responses globally
 * This should be called once during app initialization
 */
export function setupAxiosInterceptors() {
  // Response interceptor
  axios.interceptors.response.use(
    (response) => {
      // Reset logout flag on successful responses
      if (isLoggingOut && response.status >= 200 && response.status < 300) {
        isLoggingOut = false
        if (logoutTimer) {
          clearTimeout(logoutTimer)
          logoutTimer = null
        }
      }
      return response
    },
    (error) => {
      if (error?.response?.status === 403) {
        const requestUrl = error?.config?.url || ''
        const isSupportTwoFactorApi =
          typeof requestUrl === 'string' && requestUrl.includes('/api/secure/cc/support/twofactor')
        const isSupportTwoFactorRequired = error?.response?.data?.error === 'two_factor_required'
        if (!isSupportTwoFactorApi && isSupportTwoFactorRequired) {
          return handleSupportTwoFactorRequired(error)
        }
      }

      // Handle 401 Unauthorized responses (session expired, etc.)
      // Do not run logout/modal for login endpoint 401s (wrong email/password)
      if (error?.response?.status === 401) {
        const requestUrl = error?.config?.url || ''
        const loginPath = getLoginPath()
        const isLoginRequest = typeof requestUrl === 'string' && requestUrl.includes(loginPath)
        if (!isLoginRequest && wasRequestAuthenticated(error?.config)) {
          return handle401Unauthorized(error)
        }
      }
      return Promise.reject(error)
    }
  )
  
}

/**
 * Get the event name for login modal (for components to listen)
 * @returns {string} Event name
 */
export function getLoginModalEventName() {
  return LOGIN_MODAL_EVENT
}

export function getSupportTwoFactorRequiredEventName() {
  return SUPPORT_TWO_FACTOR_EVENT
}

/**
 * Cleanup function to remove interceptors (useful for testing)
 * Note: axios doesn't provide a direct way to remove interceptors
 * This resets the internal state
 */
export function removeAxiosInterceptors() {
  isLoggingOut = false
  if (logoutTimer) {
    clearTimeout(logoutTimer)
    logoutTimer = null
  }
}
